import { useState, useEffect } from 'react'
import { MatchesService } from '@/services/matches'
import { Match, NewMatch } from '@/types/admin'
import { MatchesTable } from '@/components/admin/matches/MatchesTable'
import { MatchDialog } from '@/components/admin/matches/MatchDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlusCircle, Search, Filter } from 'lucide-react'
import { toast } from 'sonner'

export default function MatchesPage() {
  const [matches, setMatches] = useState<Match[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingMatch, setEditingMatch] = useState<Match | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchMatches = async () => {
    try {
      const data = await MatchesService.getAll()
      setMatches(data)
    } catch (error) {
      toast.error('Erro ao carregar matches')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMatches()
  }, [])

  const handleCreate = () => {
    setEditingMatch(null)
    setDialogOpen(true)
  }

  const handleEdit = (match: Match) => {
    setEditingMatch(match)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: NewMatch) => {
    try {
      if (editingMatch) {
        await MatchesService.update(editingMatch.id, data)
        toast.success('Match atualizado')
      } else {
        await MatchesService.create(data)
        toast.success('Match criado')
      }
      fetchMatches()
    } catch (error) {
      toast.error('Erro ao salvar match')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza?')) return
    try {
      await MatchesService.delete(id)
      toast.success('Match excluído')
      fetchMatches()
    } catch (error) {
      toast.error('Erro ao excluir')
    }
  }

  // Filter Logic
  const filteredMatches = matches.filter((match) => {
    if (statusFilter === 'all') return true
    return match.status === statusFilter
  })

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            Gerenciar Matches
          </h1>
          <p className="text-slate-500 mt-1">
            Conexões manuais entre vagas e educadores.
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-brand-emerald hover:bg-emerald-600"
        >
          <PlusCircle className="mr-2 h-4 w-4" /> Novo Match
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm flex items-center justify-end">
        <div className="w-full md:w-56">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <SelectValue placeholder="Filtrar por Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="sent">Enviado</SelectItem>
              <SelectItem value="interview">Entrevista</SelectItem>
              <SelectItem value="hired">Contratado</SelectItem>
              <SelectItem value="rejected">Não Avançou</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-emerald mx-auto mb-4"></div>
          <p className="text-slate-500">Carregando matches...</p>
        </div>
      ) : (
        <MatchesTable
          matches={filteredMatches}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <MatchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        match={editingMatch}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
